import type { LocationPoint } from './planning.ts'

export interface SavedPlace {
  id: string
  name: string
  point: LocationPoint
}

export interface SavedPlacesState {
  places: SavedPlace[]
  writable: boolean
}

type SavedPlacesStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

const collectionKey = 'meet-halfway.saved-places'
const legacyPlaces = [
  { id: 'home', name: 'Home', key: 'meet-halfway.home' },
  { id: 'work', name: 'Work', key: 'meet-halfway.work' },
] as const

export function loadSavedPlaces(storage: SavedPlacesStorage): SavedPlacesState {
  let stored: string | null
  try {
    stored = storage.getItem(collectionKey)
  } catch {
    return { places: [], writable: false }
  }

  if (stored !== null) {
    const places = parseCollection(stored)
    return places ? { places, writable: true } : { places: readLegacyPlaces(storage).places, writable: false }
  }

  const legacy = readLegacyPlaces(storage)
  const places = legacy.places
  if (!legacy.complete) return { places, writable: false }
  if (places.length === 0) return { places, writable: true }
  if (!persistSavedPlaces(storage, places)) return { places, writable: true }
  for (const legacy of legacyPlaces) {
    try {
      storage.removeItem(legacy.key)
    } catch {
      // The versioned collection is authoritative once written; stale legacy values are harmless.
    }
  }
  return { places, writable: true }
}

export function persistSavedPlaces(storage: SavedPlacesStorage, places: SavedPlace[]): boolean {
  try {
    const stored = storage.getItem(collectionKey)
    if (stored !== null && parseCollection(stored) === null) return false
    storage.setItem(collectionKey, JSON.stringify({ version: 1, places }))
    return true
  } catch {
    return false
  }
}

function readLegacyPlaces(storage: Pick<Storage, 'getItem'>): { places: SavedPlace[]; complete: boolean } {
  const places: SavedPlace[] = []
  let complete = true
  for (const legacy of legacyPlaces) {
    let stored: string | null
    try {
      stored = storage.getItem(legacy.key)
    } catch {
      complete = false
      continue
    }
    try {
      const point = parsePoint(JSON.parse(stored ?? 'null'))
      if (point) places.push({ id: legacy.id, name: legacy.name, point })
    } catch {
      // Malformed legacy values are ignored and removed after any valid values migrate.
    }
  }
  return { places, complete }
}

function parseCollection(stored: string): SavedPlace[] | null {
  try {
    const value: unknown = JSON.parse(stored)
    if (!isRecord(value) || value.version !== 1 || !Array.isArray(value.places)) return null
    const places = value.places.map(parsePlace)
    if (places.some((place) => place === null)) return null
    const validPlaces = places as SavedPlace[]
    const ids = new Set(validPlaces.map((place) => place.id))
    return ids.size === validPlaces.length ? validPlaces : null
  } catch {
    return null
  }
}

function parsePlace(value: unknown): SavedPlace | null {
  if (!isRecord(value)
    || typeof value.id !== 'string' || !value.id || value.id.length > 100
    || typeof value.name !== 'string' || !value.name.trim() || value.name.length > 80) return null
  const point = parsePoint(value.point)
  return point ? { id: value.id, name: value.name, point } : null
}

function parsePoint(value: unknown): LocationPoint | null {
  if (!isRecord(value)
    || typeof value.displayName !== 'string' || !value.displayName || value.displayName.length > 300
    || typeof value.type !== 'string' || !value.type || value.type.length > 100
    || typeof value.lat !== 'number' || !Number.isFinite(value.lat) || value.lat < -90 || value.lat > 90
    || typeof value.lon !== 'number' || !Number.isFinite(value.lon) || value.lon < -180 || value.lon > 180) return null
  return { displayName: value.displayName, type: value.type, lat: value.lat, lon: value.lon }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}
